import React from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import ImageWithFooter from './ImageWithFooter';
import { isVisible } from '../utils/dynamicFormUtil';
import { useParams } from 'react-router-dom';
import { useProjects } from '../context/ProjectContext';

const ViewEngagementModal = ({ show, onClose, forms, engagement, handleImageClick = () => {}, onValidate, onReject }) => {
  const [remark, setRemark] = React.useState('');
  const [headerCollapsed, setHeaderCollapsed] = React.useState(true);
  const { projectId } = useParams();
  const { projects } = useProjects();
  
  // Detect if opened from validation page via prop
  const isValidationPage = engagement?.fromValidationPage;
  
  // Get current project settings
  const currentProject = projects.find(p => p.id === parseInt(projectId, 10));
  const hasValidation = currentProject?.settings?.validation;
  const hasApproval = currentProject?.settings?.approval;
  // 1) Build previewValues-like map: {[field.id]: value}
  const idKeyedValues = React.useMemo(() => {
    const m = {};
    (forms || []).forEach(form => {
      (form.fields || []).forEach(f => {
        m[String(f.id)] = engagement?.fields?.[f.name];
      });
    });
    return m;
  }, [forms, engagement]);

  return (
    <Modal show={show} onHide={onClose} size="lg" fullscreen="md-down" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ width: '100%', cursor: 'pointer' }} onClick={() => setHeaderCollapsed(c => !c)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>
              <strong>Full Name:</strong>{' '}
              <span className="text-muted">
                {`${engagement?.User?.firstName || ''} ${engagement?.User?.lastName || ''}`.trim() || '—'}
              </span>
            </span>
            <span style={{ fontSize: '1rem', marginLeft: '1rem' }}>
              {headerCollapsed ? '▼' : '▲'}
            </span>
          </div>
          {hasValidation && (
            <>
              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                Validator: {engagement?.validatorName || '—'}
              </div>
              {engagement?.validationRemarks && (
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                  Validation Remarks: {engagement.validationRemarks}
                </div>
              )}
              {engagement?.validationTime && (
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                  Validation Time: {new Date(engagement.validationTime).toLocaleString()}
                </div>
              )}
            </>
          )}
          {hasApproval && (
            <>
              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                Approver: {engagement?.approverName || '—'}
              </div>
              {engagement?.approvalRemarks && (
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                  Approval Remarks: {engagement.approvalRemarks}
                </div>
              )}
              {engagement?.approvalTime && (
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                  Approval Time: {new Date(engagement.approvalTime).toLocaleString()}
                </div>
              )}
            </>
          )}
          {!headerCollapsed && (
            <>
              <div>
                <small className="text-muted">
                  Synced Date: {engagement?.createdAt ? new Date(engagement.createdAt).toLocaleString() : '—'}
                </small>
              </div>
              <div>
                <small className="text-muted">
                  Submitted Date:{' '}
                  {engagement?.submittedAt ? new Date(engagement.submittedAt).toLocaleString() : '—'}
                </small>
              </div>
              <div>
                <small className="text-muted">App Version: {engagement?.fields?.appVersion || 'unknown'}</small>
              </div>
              <div>
                <small className="text-muted">Signature</small>
                {engagement?.signature && (
                  <img
                    src={engagement?.signature}
                    alt="Signature"
                    style={{ maxHeight: 120, objectFit: 'contain', display: 'block' }}
                  />
                )}
              </div>
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        className="d-flex flex-column gap-3"
        style={{ height: '100%', maxHeight: '80vh', overflowY: 'auto' }}
      >
        {/* 2) Use idKeyedValues for all visibility checks */}
        {forms
          .filter(form => isVisible(form, idKeyedValues))
          .map(form => (
            <div key={form.id} className="mb-4">
              <h5 className="border-bottom pb-1 mb-3">{form.name}</h5>

              {(form.fields || [])
                .slice()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map(field => {
    if (!isVisible(field, idKeyedValues) || field.type === 'label') return null;

                  // 3) Display value from the same id-keyed map
                  const value = idKeyedValues[String(field.id)];

                  return (
                    <Form.Group key={field.name} className="mb-3">
                      <Form.Label><strong>{field.label}</strong></Form.Label>
                      <div>
                        {field.type === 'image' ? (
                          value?.uri?.[0] && (
                            <div
                              className="image-container"
                              onClick={() => handleImageClick(value?.uri[0], value?.location)}
                              style={{ cursor: 'pointer' }}
                            >
                              <ImageWithFooter
                                src={value?.uri?.[0] || null}
                                alt={value?.uri?.[0] ? field.label : null}
                                footer={value?.location}
                                fluid
                                rounded
                                thumbnail
                                style={{ width: 300 }}
                              />
                            </div>
                          )
                        ) : field.type === 'address' && value ? (
                          <div>
                            {value.street && <div>Street: {value.street}</div>}
                            {value.barangay && <div>Barangay: {value.barangay}</div>}
                            {value.city && <div>City: {value.city}</div>}
                            {value.province && <div>Province: {value.province}</div>}
                          </div>
                        ) : (
                          <div>
                            {field.type === 'checkbox' 
                              ? (value ? 'True' : 'False') 
                              : (value ?? '—')
                            }
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  );
                })}
            </div>
          ))}
        {/* Validation page actions */}
        {isValidationPage && (
          <div className="mt-4">
            <div className="mb-2">
              <Form.Label>Remark (required for rejection)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={remark}
                onChange={e => setRemark(e.target.value)}
                placeholder="Enter reason for rejection"
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="success" onClick={() => {
                onValidate?.(engagement, remark);
                setRemark(''); // Reset remark field after validation
              }}>Validate</Button>
              <Button
                variant="danger"
                disabled={!remark.trim()}
                onClick={() => {
                  onReject?.(engagement, remark);
                  setRemark(''); // Reset remark field after rejection
                }}
              >
                Reject
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ViewEngagementModal;
